import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Key, ArrowLeft, Check, Building2, Save } from 'lucide-react';
import CustomFieldsRenderer from '../components/CustomFieldsRenderer';

export default function ProfilePage() {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [profileForm, setProfileForm] = useState({ full_name: '', company_name: '', contact_phone: '', extras: {} });
  const navigate = useNavigate();
  const user = api.getUser();

  useEffect(() => {
    if (!user) return;
    api.request('/auth/me').then(p => {
      setProfile(p);
      setProfileForm({
        full_name: p.full_name || '',
        company_name: p.company_name || '',
        contact_phone: p.contact_phone || '',
        extras: p.profile_extras || {},
      });
    }).catch(() => {});
    api.getPublicAssessments().then(setAssessments).catch(() => setAssessments([]));
    // eslint-disable-next-line
  }, []);

  // Build a deduplicated list of custom fields aggregated across all active
  // assessments — so the user can see / edit every field that may apply to them.
  const aggregatedFields = useMemo(() => {
    const map = new Map();
    for (const a of assessments) {
      const cf = a.company_info_schema?.custom_fields || [];
      for (const f of cf) {
        // Skip consents — they're one-off, not persisted on profile
        if ((f.type || '').toLowerCase() === 'consent') continue;
        if (!map.has(f.key)) map.set(f.key, f);
      }
    }
    return Array.from(map.values());
  }, [assessments]);

  if (!user) { navigate('/login'); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!oldPw || !newPw) return;
    if (newPw.length < 6) { setError('Password must be at least 6 characters'); return; }
    setSaving(true); setMsg(''); setError('');
    try {
      await api.changePassword(oldPw, newPw);
      setMsg('Password changed successfully!');
      setOldPw(''); setNewPw('');
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true); setProfileMsg('');
    try {
      const updated = await api.request('/auth/me', {
        method: 'PUT',
        body: JSON.stringify({
          full_name: profileForm.full_name,
          company_name: profileForm.company_name,
          contact_phone: profileForm.contact_phone,
          profile_extras: profileForm.extras,
        }),
      });
      setProfile(updated);
      setProfileMsg('Profile saved.');
      setTimeout(() => setProfileMsg(''), 3000);
    } catch (err) {
      setProfileMsg(err.message || 'Failed to save');
    }
    setProfileSaving(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-8 pb-12">
      <div className="max-w-2xl mx-auto px-6 space-y-6">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center text-white text-lg font-bold">
              {(user.full_name || 'U')[0].toUpperCase()}
            </div>
            <div>
              <p className="text-gray-900 font-semibold">{user.full_name}</p>
              <p className="text-gray-500 text-sm">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Profile data — common + aggregated custom fields */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-600" /> Profile Data
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            This data is auto-saved when you complete an assessment and pre-fills future ones.
          </p>

          {profileMsg && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded mb-4 text-sm">{profileMsg}</div>}

          <form onSubmit={handleSaveProfile} className="space-y-5">
            <div>
              <label className="block text-sm text-gray-500 mb-1">Full Name</label>
              <input value={profileForm.full_name} onChange={e => setProfileForm({ ...profileForm, full_name: e.target.value })}
                data-testid="profile-full-name-input"
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Company Name</label>
              <input value={profileForm.company_name} onChange={e => setProfileForm({ ...profileForm, company_name: e.target.value })}
                data-testid="profile-company-input"
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">Phone</label>
              <input value={profileForm.contact_phone} onChange={e => setProfileForm({ ...profileForm, contact_phone: e.target.value })}
                data-testid="profile-phone-input"
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>

            {/* Aggregated custom fields */}
            {aggregatedFields.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Additional Information</p>
                <CustomFieldsRenderer
                  fields={aggregatedFields}
                  values={profileForm.extras}
                  onChange={(k, v) => setProfileForm(prev => ({ ...prev, extras: { ...prev.extras, [k]: v } }))}
                />
              </div>
            )}

            <button type="submit" disabled={profileSaving} data-testid="save-profile-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-5 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-50">
              <Save className="w-4 h-4" /> {profileSaving ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Change password */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-emerald-600" /> Change Password
          </h2>

          {msg && <div className="bg-green-50 text-green-600 px-4 py-2 rounded-lg text-sm mb-4 flex items-center gap-2"><Check className="w-4 h-4" />{msg}</div>}
          {error && <div className="bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm mb-4">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-500 mb-1">Current Password</label>
              <input type="password" value={oldPw} onChange={e => setOldPw(e.target.value)} data-testid="old-password-input"
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-500 mb-1">New Password</label>
              <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} data-testid="new-password-input"
                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <button type="submit" disabled={saving} data-testid="change-password-btn"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50">
              {saving ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
